import { API_CONFIG } from "config/api.config";

const AUTH_BASE = `${API_CONFIG.baseUrl}/public/clientzone/auth`;

export type ClientzoneSessionResponse = {
  access_token: string;
  expires_at: number;
  customer_id: number;
  token_type: string;
};

export type ClientzoneSessionStatus = {
  customer_id: number;
  expires_at: number;
  token_type: string;
};

export type ClientzoneOtpPreview = {
  can_request: boolean;
  message: string;
  otp_phone_display?: string | null;
  dev_phone_display?: string | null;
};

const parseJson = async (response: Response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

export async function previewClientzoneOtpDestination(
  identifier: string
): Promise<ClientzoneOtpPreview> {
  const response = await fetch(`${AUTH_BASE}/otp/preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ identifier: String(identifier || "").trim() }),
  });
  const data = await parseJson(response);
  if (!response.ok) {
    throw new Error(data?.error || "Unable to check login details.");
  }
  return data as ClientzoneOtpPreview;
}

export async function requestClientzoneOtp(identifier: string): Promise<{ message: string }> {
  const response = await fetch(`${AUTH_BASE}/otp/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ identifier: String(identifier || "").trim() }),
  });
  const data = await parseJson(response);
  if (!response.ok) {
    throw new Error(data?.error || "Unable to send login code.");
  }
  return data;
}

export async function verifyClientzoneOtp(
  identifier: string,
  otp: string
): Promise<ClientzoneSessionResponse> {
  const response = await fetch(`${AUTH_BASE}/otp/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      identifier: String(identifier || "").trim(),
      otp: String(otp || "").trim(),
    }),
  });
  const data = await parseJson(response);
  if (!response.ok) {
    throw new Error(data?.error || "Invalid or expired login code.");
  }
  return data as ClientzoneSessionResponse;
}

export async function fetchClientzoneSession(accessToken: string): Promise<ClientzoneSessionStatus> {
  const response = await fetch(`${AUTH_BASE}/session`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });
  const data = await parseJson(response);
  if (!response.ok) {
    throw new Error(data?.error || "Session expired.");
  }
  return data as ClientzoneSessionStatus;
}
